import {html, HTMLTemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {classMap} from "lit/directives/class-map.js";
import {when} from "lit/directives/when.js";
import {container} from "tsyringe";
import {Entity} from "../../../domain/entities/entity";
import {StringHelper} from "../../../helpers/string.helper";
import {EntityManager} from "../../../persistence/entity.manager";
import {ClassOf} from "../../../types/class-of.type";
import {AbstractInputComponent} from "../abstract-input.component";
import {EntityLiveRefreshController} from "../../_reactive-controllers/entity-live-refresh.controller";
import {RxjsLifecycleController} from "../../_reactive-controllers/rxjs-lifecycle.controller";
import {ComboboxItem} from "./combobox-item.interface";
import {repeat} from "lit/directives/repeat.js";
import {StyleInfo, styleMap} from "lit/directives/style-map.js";
import {toPopoverListItems} from "../combobox-utils/to-popover-list-items";
import {shouldCloseOnFocusOut} from "../combobox-utils/should-close-on-focus-out";
import {closePopoverOnEscape} from "../combobox-utils/close-popover-on-escape";
import {setupOutsideClickHandler, closeOtherPopovers} from "../combobox-utils/setup-outside-click-handler";
import {SingleSelectionController} from "@o2d/popover-list";
import type {ItemSelectedDetail, PopoverListItem} from "@o2d/popover-list";
import "@o2d/popover-list";
import {itemColorStyle} from "../../../helpers/header-item-style.helper";

@customElement(`ha-combobox`)
export class HaCombobox extends AbstractInputComponent {

    @property({type: Object})
    public entity: ClassOf<Entity> | null = null;

    @property({type: Array})
    public items: ComboboxItem[] = [];

    /**
     * Optional predicate to restrict which of the fetched .entity= items are
     * actually offered — e.g. scoping the issue-type filter's options to what a
     * given view can structurally contain. Default null (no-op) keeps every
     * other .entity= consumer in the app unaffected.
     */
    @property({type: Object})
    public itemFilter: ((item: ComboboxItem) => boolean) | null = null;

    @property({type: String})
    public placeholder: string = `Bitte wählen...`;
    @property({type: Boolean, attribute: `with-clear-button`})
    public withClearButton = false;
    @property({type: Boolean, attribute: `with-deleted`})
    public withDeleted = false;
    @property({type: Boolean, attribute: `with-filter`})
    public withFilter = false;
    @property({
        type: String,
        converter: value => {
            if (!StringHelper.isFilledString(value) || ![`default`, `visible`].includes(value as `default` | `visible`)) {
                return `default`;
            }

            return value as `default` | `visible`;
        }
    })
    public theme: `default` | `visible` = `default`;
    private _entities: Entity[] = [];
    private _isProcessing = false;
    private _isReloading = false;
    private _reloadPending = false;
    public readonly rxjs = new RxjsLifecycleController(this);
    private readonly _liveRefreshController = new EntityLiveRefreshController(this, {
        getEntityClass: () => this.entity,
        onReload: () => this._handleLiveReload(),
    });

    @state()
    private _selectedComboboxItem: ComboboxItem | null = null;

    private _selectionController: SingleSelectionController | null = null;

    private _value: Entity | null = null;

    @property({type: Object})
    public get value(): Entity | null {
        return this._value;
    }

    public set value(value: Entity | null) {
        if (this._value === value) {
            return;
        }

        if (this.items.length > 0) {
            this._selectedComboboxItem = this.items.find(item => item.payload === value) ?? null;
        }

        this._value = value;
    }

    private get _filteredItems(): ComboboxItem[] {
        return this.items.filter(item => item !== this._selectedComboboxItem)
    }

    private get _isValueEntity(): boolean {
        return !!this.entity;
    }

    public connectedCallback() {
        super.connectedCallback();
        if (!!this.entity) {
            this._loadEntityItems(this.entity).then();
        }
    }

    public render(): unknown {

        switch (this.theme) {
            case `visible`:
                return this._renderThemeVisible();
            default:
                const selectedContainerClassMap = {
                    selected: true
                }

                let items = this.items;
                if (!!this.value) {
                    // `this.value` is either an Entity, a ComboboxItem, OR — when
                    // a static `items` selection runs through `_onItemSelected` —
                    // the raw `payload` primitive. Prefer `_selectedComboboxItem`
                    // (always a structured ComboboxItem) for the kebab-case marker
                    // class to avoid a `(string).title === undefined` crash.
                    const valueAsString = this.value instanceof Entity
                        ? this.value.toString()
                        : this._selectedComboboxItem?.title ?? (this.value as ComboboxItem)?.title ?? String(this.value);
                    // @ts-ignore
                    selectedContainerClassMap[`value-${StringHelper.toKebabCase(valueAsString)}`] = true;
                    if (typeof this.value === 'object' && this.value !== null && ('id' in this.value || 'uid' in this.value)) {
                        const id = 'uid' in this.value ? this.value.uid : (this.value as ComboboxItem).id;
                        items = items.filter(item => item.id !== id)
                    } else if (this._selectedComboboxItem) {
                        // Primitive payload case: drop the selected entry from the dropdown.
                        items = items.filter(item => item.id !== this._selectedComboboxItem!.id);
                    }
                }

                return html`
                    ${when(!!this.label, () => html`
                        <label @click=${this._onClickCombobox}>${this.label}</label>
                    `)}
                    <div class="wrapper"
                         @keydown=${this._onKeyDown}
                         @focusout=${this._onFocusOut}
                    >
                        <div class="wrapper-selected" style=${styleMap(this._selectedItemStyleMap())}>
                            <div class=${classMap(selectedContainerClassMap)}
                                 @click=${this._onClickCombobox}
                                 tabindex="0"
                            >
                                ${this._renderLabel()}
                            </div>
                            ${when(this.withClearButton && !!this.value, () => html`
                                <div class="button clear-selection"
                                     @click=${this._clearSelection}
                                >
                                    <i class="icon icon-xmark clear-selection"></i>
                                </div>
                            `)}
                            <div class="button open-submenu"
                                 @click=${this._onClickCombobox}
                            >
                                <i class="icon icon-chevron-down"></i>
                            </div>
                        </div>
                        <o2d-popover-list
                            popover="manual"
                            ?filterable=${this.withFilter}
                            ?focus-filter=${this.withFilter}
                            .items=${toPopoverListItems(items)}
                            .itemRenderer=${this._renderPopoverItem}
                        ></o2d-popover-list>
                    </div>
                    ${this._renderHelperText()}
                    ${this._renderErrors()}
                `;
        }
    }

    /** Sets up anchor positioning, instantiates SingleSelectionController, and binds item-selected listener on host. */
    public firstUpdated(): void {
        const popover = this.querySelector('o2d-popover-list') as HTMLElement | null;
        const trigger = this.querySelector('.wrapper-selected') as HTMLElement | null;
        if (popover && trigger) {
            trigger.style.setProperty('anchor-name', `--${popover.id}`);
        }
        if (popover) {
            this._selectionController = new SingleSelectionController(this, popover);
            setupOutsideClickHandler(this, popover);
        }
        // Listen on host because SingleSelectionController dispatches item-selected on the host,
        // not on o2d-popover-list — events bubble up, not down to children.
        this.addEventListener('item-selected', (evt: Event) => this._onItemSelected(evt as CustomEvent<ItemSelectedDetail>));
    }

    /**
     * Handles item-selected event from SingleSelectionController.
     * Resolves entity if entity mode is active, then dispatches value-changed.
     * Controller handles hidePopover() automatically — no manual close needed.
     */
    private async _onItemSelected(evt: CustomEvent<ItemSelectedDetail>): Promise<void> {
        if (this._isProcessing) return;
        this._isProcessing = true;
        try {
            const popoverItem = evt.detail.item;
            this._selectedComboboxItem = this.items.find(item => item.id === popoverItem.id) ?? null;
            this.value = popoverItem.payload as Entity | null;

            if (this.entity && popoverItem) {
                let entityValue = this._entities.find(entity => entity.uid === popoverItem.id) ?? null;
                if (!entityValue) {
                    entityValue = (await container.resolve(EntityManager).getRepositoryForEntity(this.entity)?.findOneById(popoverItem.id)) ?? null;
                }

                if (!entityValue) {
                    throw new Error(`Es gibt keine Ressource des Types ${StringHelper.toKebabCase(this.entity.name)} mit der UID ${popoverItem.id}`);
                }

                this.dispatchEvent(
                    new CustomEvent<{ value: Entity | null }>(
                        `value-changed`,
                        {detail: {value: entityValue}}
                    )
                );
            } else {
                this.dispatchEvent(
                    new CustomEvent<{ value: ComboboxItem | null }>(
                        `value-changed`,
                        {detail: {value: this._selectedComboboxItem}}
                    )
                );
            }
        } finally {
            this._isProcessing = false;
        }
    }

    private _clearSelection(): void {
        this.value = null;
        this._onValueChanged(
            new CustomEvent<{ value: ComboboxItem | null }>(`value-changed`, {
                detail: {
                    value: null
                }
            })
        ).then();
    }

    /**
     * Reloads entity items in response to MessageBroker entity.* events.
     * Clears value when the currently selected entity has disappeared after the reload.
     * Coalescing and stale-write guard are handled by _runReloadWithCoalescing.
     */
    private _handleLiveReload(): Promise<void> {
        return this._runReloadWithCoalescing(async () => {
            if (!this.entity) {
                return;
            }
            await this._loadEntityItems(this.entity);
            if (!this.isConnected) {
                return;
            }
            if (this._value !== null && !this.items.find(item => item.payload === this._value)) {
                this.value = null;
                this.dispatchEvent(
                    new CustomEvent<{ value: Entity | null }>(
                        `value-changed`,
                        {detail: {value: null}}
                    )
                );
            }
        });
    }

    /**
     * Coalesces concurrent reload calls: at most one reload runs at a time,
     * and at most one additional reload is queued (collapsing bursts of events).
     */
    private async _runReloadWithCoalescing(fn: () => Promise<void>): Promise<void> {
        if (this._isReloading) {
            this._reloadPending = true;
            return;
        }
        this._isReloading = true;
        try {
            await fn();
        } finally {
            this._isReloading = false;
            if (this._reloadPending) {
                this._reloadPending = false;
                queueMicrotask(() => { void this._runReloadWithCoalescing(fn); });
            }
        }
    }

    private async _loadEntityItems(entityClass: ClassOf<Entity>): Promise<void> {
        const repository = container.resolve(EntityManager).getRepositoryForEntity(entityClass);
        this._entities = ((await repository?.findAll([], -1, 0, true)) ?? []) as Entity[];
        const items: ComboboxItem[] = [];

        for (const entity of this._entities) {
            const item: ComboboxItem = {
                id: entity.uid,
                title: entity.toString(),
                color: entity.color,
                textColor: entity.textColor,
                payload: entity
            }
            if (!this.itemFilter || this.itemFilter(item)) {
                items.push(item);
            }
        }

        items.sort(
            (itemA, itemB) => itemA.title.toLocaleLowerCase().localeCompare(itemB.title.toLocaleLowerCase())
        );

        this.items = items;

        // If value is set but not found in items, try to reload with includeDeleted
        if (this.value && !this.items.find(item => item.payload === this.value || item.id === this.value?.uid)) {
            const deletedEntity = await repository?.findOneById(this.value.uid, true);
            if (deletedEntity && (deletedEntity as any).isDeleted) {
                const deletedItem: ComboboxItem = {
                    id: deletedEntity.uid,
                    title: deletedEntity.toString() + ' (gelöscht)',
                    color: deletedEntity.color,
                    textColor: deletedEntity.textColor,
                    payload: deletedEntity,
                    disabled: true,
                    isDeleted: true
                };
                this.items = [...this.items, deletedItem];
            }
        }

        if (this._selectedComboboxItem === null && this.value !== null) {
            this._selectedComboboxItem = this.items.find(item => item.payload === this.value) ?? null;
        }
    }

    private _onClickCombobox(evt: MouseEvent): void {
        if (this.readonly) {
            return;
        }

        if ((evt.target as HTMLElement).classList.contains(`clear-selection`)) {
            this._clearSelection();
        } else {
            this._togglePopover();
        }
    }

    /** Handles Escape key to close the popover, handing focus back to the trigger. */
    private _onKeyDown(evt: KeyboardEvent): void {
        closePopoverOnEscape(this, evt);
    }

    /**
     * Handles focusout to close the popover when focus leaves the component.
     * Delayed via requestAnimationFrame so that click events on non-focusable
     * popover items (li) fire before the popover is hidden.
     */
    private _onFocusOut(evt: FocusEvent): void {
        const popover = this.querySelector('o2d-popover-list') as HTMLElement | null;
        if (!popover?.matches(':popover-open')) return;

        const relatedTarget = evt.relatedTarget;
        requestAnimationFrame(() => {
            if (popover.matches(':popover-open') && shouldCloseOnFocusOut(this, popover, relatedTarget)) {
                popover.hidePopover();
            }
        });
    }

    private async _onValueChanged(evt: CustomEvent<{ value: ComboboxItem | null }>): Promise<void> {
        let value = evt.detail.value;

        this.value = value?.payload as Entity | null;

        if (this.entity && !!value) {
            let entityValue = this._entities.find(entity => entity.uid === value?.id) ?? null;
            if (!entityValue) {
                entityValue = (await container.resolve(EntityManager).getRepositoryForEntity(this.entity)?.findOneById(value.id)) ?? null;
            }

            if (!entityValue) {
                throw new Error(`Es gibt keine Ressource des Types ${StringHelper.toKebabCase(this.entity.name)} mit der UID ${value.id}`);
            }

            this.dispatchEvent(
                new CustomEvent<{ value: Entity | null }>(
                    `value-changed`,
                    {detail: {value: entityValue}}
                )
            );
        } else {
            this.dispatchEvent(
                new CustomEvent<{ value: ComboboxItem | null }>(
                    `value-changed`,
                    {detail: {value}}
                )
            );
        }

    }

    /**
     * Renders a popover list item with deleted-entity styling when applicable.
     * `--item-color` itself lives on the <li class="item"> wrapper now
     * (o2d-popover-list's own _renderItem, one level above what this renderer
     * returns) — CSS custom properties inherit down, so nothing needs to
     * re-declare it here (Anke, 2026-08-20).
     */
    private _renderPopoverItem = (item: PopoverListItem): HTMLTemplateResult => {
        const comboboxItem = this.items.find(ci => String(ci.id) === item.id);
        if (comboboxItem?.isDeleted) {
            return html`<span class="is-deleted">${item.label}</span>`;
        }
        // Feature plan 0074: the label carries the record's text colour; ralphy.css reads it with `inherit` as
        // fallback. `styleMap` (no CSP in horstplan; in the package this would need the CSSOM path).
        return html`<span class="item-label" style=${styleMap(itemColorStyle(comboboxItem?.color ?? null, comboboxItem?.textColor ?? null))}>${item.label}</span>`;
    };

    /**
     * Builds the `--item-color` custom property for the .wrapper-selected trigger
     * (render(), default theme) from the currently selected entity's color — one
     * level above the .label span _renderLabel() returns, so it's available
     * regardless of whether a value is currently selected (Anke, 2026-08-20:
     * "bitte auch hoch hängen in .wrapper-selected").
     */
    private _selectedItemStyleMap(): Readonly<StyleInfo> {
        return itemColorStyle(this._selectedComboboxItem?.color ?? null, this._selectedComboboxItem?.textColor ?? null);
    }

    /** `--item-color` lives one level up now, on .wrapper-selected (render()) — inherits down. */
    private _renderLabel(): string | HTMLTemplateResult {
        if (!!this.value) {
            return html`
                <span class="label" data-sid=${this.value?.uid ?? `unknown`}>
                    <span class="title">${this.value instanceof Entity ? this.value.toString() : (this.value as ComboboxItem).title}</span>
                </span>
            `;
        }

        if (!!this.placeholder) {
            return html`<span class="placeholder">${this.placeholder}</span>`;
        }

        return ``;
    }

    /** Toggles popover visibility via native Popover API. Closes other open popovers first. */
    private _togglePopover(): void {
        const popover = this.querySelector('o2d-popover-list') as HTMLElement | null;
        if (!popover) return;

        if (popover.matches(':popover-open')) {
            popover.hidePopover();
        } else {
            closeOtherPopovers(popover);
            popover.showPopover();
        }
    }

    private _renderThemeVisible(): HTMLTemplateResult {

        return html`
            <div class="wrapper">
                <div class="wrapper-selected">
                    <div class="selected">
                        <span class="label value-${StringHelper.toKebabCase(this._selectedComboboxItem?.title ?? `unknown`)}"
                              data-sid=${this._selectedComboboxItem?.id ?? ``}
                              style=${styleMap(itemColorStyle(this._selectedComboboxItem?.color ?? `#ffffff`, this._selectedComboboxItem?.textColor ?? null))}>
                            <span class="title">${this._selectedComboboxItem?.title ?? `unbekannt`}</span>
                        </span>
                    </div>
                </div>    
                <div class="available-items">
                    ${repeat(this._filteredItems, item => item.id, item => {
    
                        const itemStyles: Readonly<StyleInfo> = itemColorStyle(item.color ?? null, item.textColor ?? null);

                        return html`
                            <div class="value-${StringHelper.toKebabCase(item.title)}"
                                 data-sid=${item.id}
                                 style=${styleMap(itemStyles)}
                                 @click=${() => this._onClickSelectItem(item)}
                            >
                                ${item.title}
                            </div>
                        `;
                    })}
                </div>
            </div>    
            ${this._renderHelperText()}
        `
    }

    private _onClickSelectItem(item: ComboboxItem) {
        this._selectedComboboxItem = item;
        this.value = item.payload as Entity | null;

        this.fire(this.value);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ha-combobox': HaCombobox;
    }
}